import type { components } from "../../../api/generated";
import type {
  ContactAssignmentConfirmPayload,
  ContactAssignmentConfirmResult,
  ContactAssignmentPreviewItem,
  ContactAssignmentPreviewPayload,
  ContactAssignmentPreviewResult,
  ContactWorkbenchCreateCollaborationTaskPayload,
  ContactWorkbenchCreateCollaborationTaskResult,
  ContactWorkbenchDetail,
  ContactWorkbenchGiftCount,
  ContactWorkbenchItem,
  ContactWorkbenchListParams,
  ContactWorkbenchListFilters,
  ContactWorkbenchLatestRecordLabel,
  ContactWorkbenchMetadata,
  ContactWorkbenchNlQueryParseResult,
  ContactWorkbenchOrganizationOption,
  ContactWorkbenchOrganizationUnitOption,
  ContactWorkbenchOwnerKind,
  ContactWorkbenchOwnerOption,
  ContactWorkbenchOwnerOptions,
  ContactWorkbenchRecordCreatePayload,
  ContactWorkbenchRecordDeleteResult,
  ContactWorkbenchRecordItem,
  ContactWorkbenchStaffOption,
  ContactWorkbenchSchoolBatchAssignmentConfirmPayload,
  ContactWorkbenchSchoolBatchAssignmentConfirmResult,
  ContactWorkbenchSchoolBatchAssignmentPreviewPayload,
  ContactWorkbenchSchoolBatchAssignmentPreviewResult,
  ContactWorkbenchTransferPayload,
  ContactWorkbenchTemporaryTaskAssignment,
  ContactWorkbenchUpdatePayload,
} from "../types";

export type ContactWorkbenchItemRaw = components["schemas"]["ContactWorkbenchItem"];
export type ContactGiftMutationRaw = components["schemas"]["ContactGiftMutation"];
export type ContactGiftItemRaw = components["schemas"]["ContactGiftItem"];
export type ContactGiftListPageRaw = components["schemas"]["ContactGiftListPage"];
export type ContactGiftOptionRaw = components["schemas"]["ContactGiftOption"];
export type ContactGiftMutationResultRaw = components["schemas"]["ContactGiftMutationResult"];
export type ContactGiftDeleteResultRaw = components["schemas"]["ContactGiftDeleteResult"];
export type ContactWorkbenchListPageRaw = components["schemas"]["ContactWorkbenchListPage"];
export type ContactWorkbenchMetadataRaw = components["schemas"]["ContactWorkbenchMetadata"];
export type ContactWorkbenchStaffOptionRaw = components["schemas"]["ContactWorkbenchStaffOption"];
export type ContactWorkbenchRecordTypeOptionRaw =
  components["schemas"]["ContactWorkbenchRecordTypeOption"];
export type ContactWorkbenchRecordLabelGroupRaw =
  components["schemas"]["ContactWorkbenchRecordLabelGroup"];
export type ContactWorkbenchRecordLabelOptionRaw =
  components["schemas"]["ContactWorkbenchRecordLabelOption"];
export type ContactWorkbenchTransferMutationRaw =
  components["schemas"]["ContactWorkbenchTransferMutation"];
export type ContactWorkbenchTransferResultRaw =
  components["schemas"]["ContactWorkbenchTransferResult"];
export type ContactWorkbenchDetailRaw = components["schemas"]["ContactWorkbenchDetail"];
export type ContactWorkbenchUpdateMutationRaw =
  components["schemas"]["ContactWorkbenchUpdateMutation"];
export type ContactWorkbenchOrganizationOptionRaw =
  components["schemas"]["ContactWorkbenchOrganizationOption"];
export type ContactWorkbenchOrganizationUnitOptionRaw =
  components["schemas"]["ContactWorkbenchOrganizationUnitOption"];
export type ContactWorkbenchSchoolBatchAssignmentPreviewMutationRaw =
  components["schemas"]["ContactWorkbenchSchoolBatchAssignmentPreviewMutation"];
export type ContactWorkbenchSchoolBatchAssignmentConfirmMutationRaw =
  components["schemas"]["ContactWorkbenchSchoolBatchAssignmentConfirmMutation"];
export type ContactWorkbenchSchoolBatchAssignmentPreviewResultRaw =
  components["schemas"]["ContactWorkbenchSchoolBatchAssignmentPreviewResult"];
export type ContactWorkbenchSchoolBatchAssignmentConfirmResultRaw =
  components["schemas"]["ContactWorkbenchSchoolBatchAssignmentConfirmResult"];
export type ContactWorkbenchRecordItemRaw =
  components["schemas"]["ContactWorkbenchRecordItem"];
export type ContactWorkbenchRecordListRaw =
  components["schemas"]["ContactWorkbenchRecordList"];
export type ContactWorkbenchRecordCreateMutationRaw =
  components["schemas"]["ContactWorkbenchRecordCreateMutation"];
export type ContactWorkbenchRecordDeleteResultRaw =
  components["schemas"]["ContactWorkbenchRecordDeleteResult"];
export type ContactWorkbenchLatestRecordLabelRaw =
  components["schemas"]["ContactWorkbenchLatestRecordLabel"];
export type ContactWorkbenchGiftCountRaw = components["schemas"]["ContactWorkbenchGiftCount"];
export type ContactWorkbenchTemporaryTaskAssignmentRaw =
  components["schemas"]["ContactWorkbenchTemporaryTaskAssignment"];
export type ContactWorkbenchNlQueryParseMutationRaw =
  components["schemas"]["ContactWorkbenchNlQueryParseMutation"];
export type ContactWorkbenchNlQueryParseResultRaw =
  components["schemas"]["ContactWorkbenchNlQueryParseResult"];
export type ContactAssignmentPreviewMutationRaw =
  components["schemas"]["ContactAssignmentPreviewMutation"];
export type ContactAssignmentConfirmMutationRaw =
  components["schemas"]["ContactAssignmentConfirmMutation"];
export type ContactSelectionTaskMutationRaw =
  components["schemas"]["CollaborationContactSelectionTaskMutation"];

type ContactWorkbenchOwnersRaw = {
  primary_owner_user_ids?: number[] | null;
  primary_owner_user_names?: string[] | null;
  shared_owner_user_ids?: number[] | null;
  shared_owner_user_names?: string[] | null;
};

type ContactWorkbenchAccessRaw = {
  access_role?: ContactWorkbenchItem["accessRole"] | null;
  has_contact_permission?: boolean | null;
  can_view_sensitive_contact_info?: boolean | null;
  can_open_contact?: boolean | null;
  can_edit?: boolean | null;
  can_assign?: boolean | null;
  can_delete?: boolean | null;
  can_record?: boolean | null;
  can_delete_record?: boolean | null;
  can_manage_gift?: boolean | null;
  can_delete_gift?: boolean | null;
  can_put_into_task_pool?: boolean | null;
};

type ContactWorkbenchTemporaryTasksRaw = {
  temporary_task_assignments?: ContactWorkbenchTemporaryTaskAssignmentRaw[] | null;
};

type ContactAssignmentPreviewResultRaw = {
  total_count?: number;
  assignable_count?: number;
  skipped_count?: number;
  conflict_count?: number;
  disabled_count?: number;
  items?: ContactAssignmentPreviewItemRaw[];
};

type ContactAssignmentPreviewItemRaw = {
  contact_id?: number;
  contact_name?: string;
  is_active?: boolean;
  current_primary_user_id?: number | null;
  current_primary_user_name?: string | null;
  action?: ContactAssignmentPreviewItem["action"];
  reason?: string;
};

type ContactAssignmentConfirmResultRaw = {
  updated_count?: number;
  skipped_count?: number;
  log_count?: number;
};

type ContactSelectionTaskResultRaw = {
  task?: {
    id?: number;
    task_status?: string;
  };
  import_result?: {
    importable_count?: number;
  };
  confirm_result?: {
    inserted_count?: number;
    skipped_count?: number;
  };
};

/** 映射联系人工作台分页查询参数。 */
export function mapContactWorkbenchListQueryParams(params: ContactWorkbenchListParams) {
  return {
    page: params.page,
    page_size: params.pageSize,
    keyword: normalizeOptionalText(params.keyword),
    owner_user_id: params.ownerUserId,
    owner_user_ids: params.ownerUserIds,
    shared_owner_user_ids: params.sharedOwnerUserIds,
    temporary_handler_user_ids: params.temporaryHandlerUserIds,
    view_scope: params.viewScope,
    collaboration_scope: params.collaborationScope,
    collaboration_task_type: params.collaborationTaskType,
    contact_star: normalizeOptionalText(params.contactStar),
    contact_star_unset: params.contactStarUnset,
    record_labels: params.recordLabels,
    follow_up_labels: params.followUpLabels,
    org_id: params.orgId,
    org_ids: params.orgIds,
    province: normalizeOptionalText(params.province),
    provinces: params.provinces,
    city: normalizeOptionalText(params.city),
    district: normalizeOptionalText(params.district),
    is_active: params.isActive,
    nl_query: normalizeOptionalText(params.nlQuery),
    sort_field: params.sortField,
    sort_direction: params.sortDirection,
  };
}

/** 映射归属销售列头候选：与当前表格筛选对齐，不含分页与归属销售自身条件。 */
export function mapContactWorkbenchOwnerOptionsQueryParams(
  params: ContactWorkbenchListFilters,
) {
  return {
    keyword: normalizeOptionalText(params.keyword),
    view_scope: params.viewScope,
    collaboration_scope: params.collaborationScope,
    collaboration_task_type: params.collaborationTaskType,
    contact_star: normalizeOptionalText(params.contactStar),
    contact_star_unset: params.contactStarUnset,
    record_labels: params.recordLabels,
    follow_up_labels: params.followUpLabels,
    org_id: params.orgId,
    org_ids: params.orgIds,
    province: normalizeOptionalText(params.province),
    provinces: params.provinces,
    city: normalizeOptionalText(params.city),
    district: normalizeOptionalText(params.district),
    is_active: params.isActive,
    nl_query: normalizeOptionalText(params.nlQuery),
  };
}

/** 映射联系人工作台自然语言查询解析请求。 */
export function mapContactWorkbenchNlQueryParsePayload(
  query: string,
): ContactWorkbenchNlQueryParseMutationRaw {
  return {
    query: query.trim(),
  };
}

/** 映射联系人归属销售批量转移请求。 */
export function mapContactWorkbenchTransferPayload(
  payload: ContactWorkbenchTransferPayload,
): ContactWorkbenchTransferMutationRaw {
  return {
    contact_ids: payload.contactIds,
    target_user_id: payload.targetUserId,
    current_user_id: payload.currentUserId,
  };
}

/** 映射联系人长期分配预览请求。 */
export function mapContactAssignmentPreviewPayload(
  payload: ContactAssignmentPreviewPayload,
): ContactAssignmentPreviewMutationRaw {
  return {
    contact_ids: payload.contactIds,
    target_user_id: payload.targetUserId,
    relation_type: payload.relationType,
    conflict_strategy: payload.conflictStrategy,
    current_user_id: payload.currentUserId,
    source_type: payload.sourceType ?? "manual",
  };
}

/** 映射联系人长期分配确认请求。 */
export function mapContactAssignmentConfirmPayload(
  payload: ContactAssignmentConfirmPayload,
): ContactAssignmentConfirmMutationRaw {
  return {
    ...mapContactAssignmentPreviewPayload(payload),
    current_user_id: payload.currentUserId,
    permission_level: payload.permissionLevel,
    source_type: payload.sourceType ?? "manual",
    source_id: payload.sourceId,
    reason: payload.reason ?? "",
    source_snapshot_json: payload.sourceSnapshot,
  };
}

/** 映射联系人工作台投放任务池请求。 */
export function mapContactSelectionTaskPayload(
  payload: ContactWorkbenchCreateCollaborationTaskPayload,
): ContactSelectionTaskMutationRaw {
  return {
    contact_ids: payload.contactIds,
    task_type: payload.taskType,
    current_user_id: payload.currentUserId,
    task_name: payload.taskName ?? "",
    default_assigned_user_id: payload.defaultAssignedUserId,
    source_snapshot_json: payload.sourceSnapshot,
    remark: payload.remark ?? "",
  };
}

/** 映射联系人资料更新请求。 */
export function mapContactWorkbenchUpdatePayload(
  payload: ContactWorkbenchUpdatePayload,
): ContactWorkbenchUpdateMutationRaw {
  return {
    contact_name: payload.contactName,
    org_id: payload.orgId,
    main_org_unit_id: payload.mainOrgUnitId,
    mobile: normalizeOptionalText(payload.mobile),
    backup_mobile: normalizeOptionalText(payload.backupMobile),
    cert_number: normalizeOptionalText(payload.certNumber),
    wechat: normalizeOptionalText(payload.wechat),
    position_name: normalizeOptionalText(payload.positionName),
    contact_star: normalizeOptionalText(payload.contactStar),
    hobby_note: normalizeOptionalText(payload.hobbyNote),
    taste_preference_note: normalizeOptionalText(payload.tastePreferenceNote),
    gift_interest_note: normalizeOptionalText(payload.giftInterestNote),
    is_key_contact: payload.isKeyContact,
    is_active: payload.isActive,
    remark: normalizeOptionalText(payload.remark),
  };
}

/** 映射联系人动态新增请求。 */
export function mapContactWorkbenchRecordCreatePayload(
  payload: ContactWorkbenchRecordCreatePayload,
): ContactWorkbenchRecordCreateMutationRaw {
  return {
    record_type_code: payload.recordTypeCode,
    content: payload.content,
    meeting_name: normalizeOptionalText(payload.meetingName),
    meeting_desc: normalizeOptionalText(payload.meetingDesc),
    labels_snapshot: normalizeOptionalText(payload.labelsSnapshot),
    recorded_by_user_id: payload.recordedByUserId,
    attachments: (payload.attachments ?? []).map((item) => ({
      file_url: item.fileUrl,
      content: normalizeOptionalText(item.content),
    })),
  };
}

/** 映射联系人工作台元数据响应。 */
export function mapContactWorkbenchMetadata(raw: ContactWorkbenchMetadataRaw): ContactWorkbenchMetadata {
  return {
    recordTypeLabels: raw.record_type_labels ?? {},
    recordTypeOptions: (raw.record_type_options ?? []).map(mapContactWorkbenchRecordTypeOption),
    recordLabelGroups: (raw.record_label_groups ?? []).map(mapContactWorkbenchRecordLabelGroup),
    staffOptions: (raw.staff_options ?? []).map(mapContactWorkbenchStaffOption),
    contactStarOptions: raw.contact_star_options ?? [],
    canRegionSchoolBatchAssign: raw.can_region_school_batch_assign ?? false,
  };
}

export type ContactWorkbenchOwnerOptionRaw = {
  user_id: number;
  user_name?: string | null;
  contact_count?: number | null;
  owner_kind: ContactWorkbenchOwnerKind;
};

export type ContactWorkbenchOwnerOptionsRaw = {
  primary_owners?: ContactWorkbenchOwnerOptionRaw[] | null;
  shared_owners?: ContactWorkbenchOwnerOptionRaw[] | null;
  temporary_handlers?: ContactWorkbenchOwnerOptionRaw[] | null;
};

/** 映射归属销售筛选候选项。 */
export function mapContactWorkbenchOwnerOption(
  raw: ContactWorkbenchOwnerOptionRaw,
): ContactWorkbenchOwnerOption {
  return {
    userId: raw.user_id,
    userName: raw.user_name ?? "",
    contactCount: raw.contact_count ?? 0,
    ownerKind: raw.owner_kind,
  };
}

/** 映射归属销售筛选候选分组。 */
export function mapContactWorkbenchOwnerOptions(
  raw: ContactWorkbenchOwnerOptionsRaw,
): ContactWorkbenchOwnerOptions {
  return {
    primaryOwners: (raw.primary_owners ?? []).map(mapContactWorkbenchOwnerOption),
    sharedOwners: (raw.shared_owners ?? []).map(mapContactWorkbenchOwnerOption),
    temporaryHandlers: (raw.temporary_handlers ?? []).map(mapContactWorkbenchOwnerOption),
  };
}

/** 映射按学校批量分配范围预览请求. */
export function mapContactWorkbenchSchoolBatchAssignmentPreviewPayload(
  payload: ContactWorkbenchSchoolBatchAssignmentPreviewPayload,
): ContactWorkbenchSchoolBatchAssignmentPreviewMutationRaw {
  return { org_ids: payload.orgIds };
}

/** 映射按学校批量分配确认请求. */
export function mapContactWorkbenchSchoolBatchAssignmentConfirmPayload(
  payload: ContactWorkbenchSchoolBatchAssignmentConfirmPayload,
): ContactWorkbenchSchoolBatchAssignmentConfirmMutationRaw {
  return {
    org_ids: payload.orgIds,
    assignment_mode: payload.assignmentMode,
    target_user_ids: payload.targetUserIds ?? [],
    task_name: payload.taskName?.trim() || undefined,
  };
}

/** 映射按学校批量分配范围预览结果. */
export function mapContactWorkbenchSchoolBatchAssignmentPreviewResult(
  raw: ContactWorkbenchSchoolBatchAssignmentPreviewResultRaw,
): ContactWorkbenchSchoolBatchAssignmentPreviewResult {
  return {
    organizationCount: raw.organization_count,
    contactCount: raw.contact_count,
  };
}

/** 映射按学校批量分配确认结果. */
export function mapContactWorkbenchSchoolBatchAssignmentConfirmResult(
  raw: ContactWorkbenchSchoolBatchAssignmentConfirmResultRaw,
): ContactWorkbenchSchoolBatchAssignmentConfirmResult {
  return {
    ...mapContactWorkbenchSchoolBatchAssignmentPreviewResult(raw),
    targetUserCount: raw.target_user_count,
    updatedCount: raw.updated_count,
    skippedCount: raw.skipped_count,
    logCount: raw.log_count,
  };
}

/** 映射联系人工作台自然语言查询解析结果。 */
export function mapContactWorkbenchNlQueryParseResult(
  raw: ContactWorkbenchNlQueryParseResultRaw,
): ContactWorkbenchNlQueryParseResult {
  return {
    query: raw.query,
    executable: raw.executable,
    conditions: (raw.conditions ?? []).map((condition) => ({
      field: condition.field,
      operator: condition.operator,
      value: condition.value ?? undefined,
      rawText: condition.raw_text ?? undefined,
      label: condition.label ?? undefined,
    })),
    summary: raw.summary ?? [],
    messages: raw.messages ?? [],
  };
}

/** 映射联系人详情。 */
export function mapContactWorkbenchDetail(raw: ContactWorkbenchDetailRaw): ContactWorkbenchDetail {
  const ownerRaw = raw as ContactWorkbenchDetailRaw & ContactWorkbenchOwnersRaw;
  const temporaryTaskRaw = raw as ContactWorkbenchDetailRaw & ContactWorkbenchTemporaryTasksRaw;
  return {
    vectorSyncWarning: raw.vector_sync_warning ?? undefined,
    id: raw.id,
    contactName: raw.contact_name,
    mobile: raw.mobile ?? undefined,
    backupMobile: raw.backup_mobile ?? undefined,
    certNumber: raw.cert_number ?? undefined,
    wechat: raw.wechat ?? undefined,
    positionName: raw.position_name ?? undefined,
    contactStar: raw.contact_star ?? undefined,
    hobbyNote: raw.hobby_note ?? undefined,
    tastePreferenceNote: raw.taste_preference_note ?? undefined,
    giftInterestNote: raw.gift_interest_note ?? undefined,
    isKeyContact: raw.is_key_contact,
    isActive: raw.is_active,
    remark: raw.remark ?? undefined,
    orgId: raw.org_id,
    orgName: raw.org_name,
    mainOrgUnitId: raw.main_org_unit_id ?? undefined,
    mainOrgUnitName: raw.main_org_unit_name ?? undefined,
    mainOrgUnitPathName: raw.main_org_unit_path_name ?? undefined,
    ownerUserIds: raw.owner_user_ids ?? [],
    ownerUserNames: raw.owner_user_names ?? [],
    primaryOwnerUserIds: ownerRaw.primary_owner_user_ids ?? raw.owner_user_ids ?? [],
    primaryOwnerUserNames: ownerRaw.primary_owner_user_names ?? raw.owner_user_names ?? [],
    sharedOwnerUserIds: ownerRaw.shared_owner_user_ids ?? [],
    sharedOwnerUserNames: ownerRaw.shared_owner_user_names ?? [],
    temporaryTaskAssignments: (temporaryTaskRaw.temporary_task_assignments ?? []).map(
      mapContactWorkbenchTemporaryTaskAssignment,
    ),
    updatedAt: raw.updated_at,
  };
}

/** 映射联系人工作台学校候选项。 */
export function mapContactWorkbenchOrganizationOption(
  raw: ContactWorkbenchOrganizationOptionRaw,
): ContactWorkbenchOrganizationOption {
  return {
    id: raw.id,
    orgName: raw.org_name,
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
  };
}

/** 映射联系人工作台院系专业候选项。 */
export function mapContactWorkbenchOrganizationUnitOption(
  raw: ContactWorkbenchOrganizationUnitOptionRaw,
): ContactWorkbenchOrganizationUnitOption {
  return {
    id: raw.id,
    orgId: raw.org_id,
    unitName: raw.unit_name,
    unitPathName: raw.unit_path_name,
    levelNo: raw.level_no,
  };
}

/** 映射联系人动态列表项。 */
export function mapContactWorkbenchRecordItem(
  raw: ContactWorkbenchRecordItemRaw,
): ContactWorkbenchRecordItem {
  return {
    id: raw.id,
    recordTypeCode: raw.record_type_code,
    recordTypeLabel: raw.record_type_label,
    content: raw.content ?? undefined,
    recordedAt: raw.recorded_at,
    recordedByUserId: raw.recorded_by_user_id ?? undefined,
    recordedByUserName: raw.recorded_by_user_name ?? undefined,
    meetingName: raw.meeting_name ?? undefined,
    meetingDesc: raw.meeting_desc ?? undefined,
    labelsSnapshot: raw.labels_snapshot ?? undefined,
    attachments: (raw.attachments ?? [])
      .map((item) => ({
        fileUrl: item.file_url,
        content: item.content ?? undefined,
      }))
      .filter((item) => Boolean(item.fileUrl)),
  };
}

/** 映射联系人最新动态标签。 */
export function mapContactWorkbenchLatestRecordLabel(
  raw: ContactWorkbenchLatestRecordLabelRaw,
): ContactWorkbenchLatestRecordLabel {
  return {
    contactId: raw.contact_id,
    labelsSnapshot: raw.labels_snapshot,
  };
}

/** 映射联系人礼品数量异步补充项。 */
export function mapContactWorkbenchGiftCount(
  raw: ContactWorkbenchGiftCountRaw,
): ContactWorkbenchGiftCount {
  return {
    contactId: raw.contact_id,
    giftCount: raw.gift_count,
  };
}

/** 映射联系人工作台列表项。 */
/** 映射联系人动态删除结果。 */
export function mapContactWorkbenchRecordDeleteResult(
  raw: ContactWorkbenchRecordDeleteResultRaw,
): ContactWorkbenchRecordDeleteResult {
  return {
    deleted: raw.deleted,
  };
}

/** 映射联系人工作台列表项。 */
export function mapContactWorkbenchItem(raw: ContactWorkbenchItemRaw): ContactWorkbenchItem {
  const ownerRaw = raw as ContactWorkbenchItemRaw & ContactWorkbenchOwnersRaw;
  const accessRaw = raw as ContactWorkbenchItemRaw & ContactWorkbenchAccessRaw;
  const temporaryTaskRaw = raw as ContactWorkbenchItemRaw & ContactWorkbenchTemporaryTasksRaw;
  return {
    id: raw.id,
    contactName: raw.contact_name,
    mobile: raw.mobile ?? undefined,
    wechat: raw.wechat ?? undefined,
    positionName: raw.position_name ?? undefined,
    isKeyContact: raw.is_key_contact,
    contactStar: raw.contact_star ?? undefined,
    isKeyContactLabel: raw.is_key_contact_label,
    isActive: raw.is_active,
    isActiveLabel: raw.is_active_label,
    lastContactAt: raw.last_contact_at ?? undefined,
    orgId: raw.org_id,
    orgName: raw.org_name,
    orgType: raw.org_type ?? undefined,
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
    district: raw.district ?? undefined,
    mainOrgUnitId: raw.main_org_unit_id ?? undefined,
    mainOrgUnitName: raw.main_org_unit_name ?? undefined,
    mainOrgUnitPathName: raw.main_org_unit_path_name ?? undefined,
    ownerUserIds: raw.owner_user_ids ?? [],
    ownerUserNames: raw.owner_user_names ?? [],
    primaryOwnerUserIds: ownerRaw.primary_owner_user_ids ?? raw.owner_user_ids ?? [],
    primaryOwnerUserNames: ownerRaw.primary_owner_user_names ?? raw.owner_user_names ?? [],
    sharedOwnerUserIds: ownerRaw.shared_owner_user_ids ?? [],
    sharedOwnerUserNames: ownerRaw.shared_owner_user_names ?? [],
    temporaryTaskAssignments: (temporaryTaskRaw.temporary_task_assignments ?? []).map(
      mapContactWorkbenchTemporaryTaskAssignment,
    ),
    recordCount: raw.record_count,
    attachmentCount: raw.attachment_count,
    latestRecordAt: raw.latest_record_at ?? undefined,
    giftCount: raw.gift_count,
    createdAt: raw.created_at ?? undefined,
    latestRecordTypeCode: raw.latest_record_type_code ?? undefined,
    latestRecordTypeLabel: raw.latest_record_type_label,
    latestRecordContent: raw.latest_record_content ?? undefined,
    latestRecordMeetingName: raw.latest_record_meeting_name ?? undefined,
    accessRole: accessRaw.access_role ?? "none",
    hasContactPermission: accessRaw.has_contact_permission ?? false,
    canViewSensitiveContactInfo: accessRaw.can_view_sensitive_contact_info ?? false,
    canOpenContact: accessRaw.can_open_contact ?? false,
    canEdit: accessRaw.can_edit ?? false,
    canAssign: accessRaw.can_assign ?? false,
    canDelete: accessRaw.can_delete ?? false,
    canRecord: accessRaw.can_record ?? false,
    canDeleteRecord: accessRaw.can_delete_record ?? false,
    canManageGift: accessRaw.can_manage_gift ?? false,
    canDeleteGift: accessRaw.can_delete_gift ?? false,
    canPutIntoTaskPool: accessRaw.can_put_into_task_pool ?? false,
    updatedAt: raw.updated_at,
  };
}

/** 映射联系人临时任务处理关系。 */
export function mapContactWorkbenchTemporaryTaskAssignment(
  raw: ContactWorkbenchTemporaryTaskAssignmentRaw,
): ContactWorkbenchTemporaryTaskAssignment {
  return {
    itemId: raw.item_id,
    taskId: raw.task_id,
    contactId: raw.contact_id,
    taskName: raw.task_name,
    taskType: raw.task_type,
    assignmentMode: raw.assignment_mode ?? "pool",
    handlerUserId: raw.handler_user_id ?? undefined,
    handlerUserName: raw.handler_user_name ?? undefined,
    accessStatus: raw.access_status,
  };
}

/** 映射联系人长期分配预览响应。 */
export function mapContactAssignmentPreviewResult(
  raw: unknown,
): ContactAssignmentPreviewResult {
  const result = (raw ?? {}) as ContactAssignmentPreviewResultRaw;
  return {
    totalCount: result.total_count ?? 0,
    assignableCount: result.assignable_count ?? 0,
    skippedCount: result.skipped_count ?? 0,
    conflictCount: result.conflict_count ?? 0,
    disabledCount: result.disabled_count ?? 0,
    items: (result.items ?? []).map(mapContactAssignmentPreviewItem),
  };
}

/** 映射联系人长期分配确认响应。 */
export function mapContactAssignmentConfirmResult(
  raw: unknown,
): ContactAssignmentConfirmResult {
  const result = (raw ?? {}) as ContactAssignmentConfirmResultRaw;
  return {
    updatedCount: result.updated_count ?? 0,
    skippedCount: result.skipped_count ?? 0,
    logCount: result.log_count ?? 0,
  };
}

/** 映射联系人长期分配预览明细。 */
function mapContactAssignmentPreviewItem(
  raw: ContactAssignmentPreviewItemRaw,
): ContactAssignmentPreviewItem {
  return {
    contactId: raw.contact_id ?? 0,
    contactName: raw.contact_name ?? "",
    isActive: raw.is_active ?? false,
    currentPrimaryUserId: raw.current_primary_user_id ?? undefined,
    currentPrimaryUserName: raw.current_primary_user_name ?? undefined,
    action: raw.action ?? "skip",
    reason: raw.reason ?? "",
  };
}

/** 映射联系人工作台投放任务池结果。 */
export function mapContactSelectionTaskResult(
  raw: unknown,
): ContactWorkbenchCreateCollaborationTaskResult {
  const result = (raw ?? {}) as ContactSelectionTaskResultRaw;
  return {
    taskId: result.task?.id ?? 0,
    taskStatus: result.task?.task_status ?? "",
    importableCount: result.import_result?.importable_count ?? 0,
    insertedCount: result.confirm_result?.inserted_count ?? 0,
    skippedCount: result.confirm_result?.skipped_count ?? 0,
  };
}

/** 映射联系人工作台员工候选。 */
export function mapContactWorkbenchStaffOption(
  raw: ContactWorkbenchStaffOptionRaw,
): ContactWorkbenchStaffOption {
  return {
    id: raw.id,
    username: raw.username,
    displayName: raw.display_name,
    phone: raw.phone ?? undefined,
    disabled: raw.disabled,
  };
}

/** 映射联系人动态类型选项。 */
export function mapContactWorkbenchRecordTypeOption(
  raw: ContactWorkbenchRecordTypeOptionRaw,
) {
  return {
    code: raw.code,
    label: raw.label,
    legacyRecordTool: raw.legacy_record_tool ?? undefined,
    formMode: raw.form_mode ?? "content",
  };
}

/** 映射联系人动态标签分组。 */
export function mapContactWorkbenchRecordLabelGroup(
  raw: ContactWorkbenchRecordLabelGroupRaw,
) {
  return {
    groupCode: raw.group_code,
    groupLabel: raw.group_label,
    labels: (raw.labels ?? []).map(mapContactWorkbenchRecordLabelOption),
  };
}

/** 映射联系人动态标签选项。 */
export function mapContactWorkbenchRecordLabelOption(
  raw: ContactWorkbenchRecordLabelOptionRaw,
) {
  return {
    label: raw.label,
    usageCount: raw.usage_count,
  };
}

/** 归一化可选查询文本。 */
function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}
